import React from 'react';
import { Box, Card, CardContent, Chip, Divider, Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { RoomCardsCarousel } from '../CardsCarousel';

import { RootState } from 'store';
import { CountryCodeEnum } from 'features/common/types';
import { QuotationRoomWithPrice } from '../../types';
import { getBoardName, getRoomTotalLabel, GetPAXTotalsResponse, separadorDeMiles } from '../../helpers';
import { SIGN_GREEN } from '../../branding';
import { Utility } from '../Utility';

/**
 * Habitaciones y precios de UNA opción de hotel en cotizaciones de un destino.
 *
 * Reemplaza al par HotelOptionsPricePreviewDesktop (tabla) /
 * HotelOptionsPricePreviewMobile (cards con floats): un solo componente
 * responsive con el lenguaje visual del rediseño — cards redondeadas, total
 * destacado, seña como fila secundaria SIEMPRE en verde (SIGN_GREEN): la seña
 * es el gancho de venta y tiene su propio color en toda la propuesta —
 * naranja/accent = cuánto cuesta, verde = con cuánto asegurás tu lugar hoy.
 */

const EXTERNAL_ACCENT_COLOR = '#06036c';

interface Props {
	rooms: QuotationRoomWithPrice[];
	paxTotals: GetPAXTotalsResponse;
	exchangeRate: number;
	localCurrency: string;
	showLocalCurrency?: boolean;
	isExternal?: boolean;
	/**
	 * Margen bruto por habitación: SÓLO para la vista del agente. Obligatoria a
	 * propósito (sin default): quien agregue una llamada nueva tiene que decidir
	 * explícitamente, así no se filtra a la propuesta del cliente por olvido.
	 */
	showUtility: boolean;
	markup: number | null;
	/** Para la línea de "qué incluye" debajo del total. */
	includedSummary?: string;
}

interface MoneyRowProps {
	title: string;
	valueUSD: number;
	exchangeRate: number;
	localCurrency: string;
	showLocalCurrency?: boolean;
	primaryInLocalCurrency: boolean;
	highlight?: boolean;
	sign?: boolean;
	accentColor?: string;
	/** Línea de "qué incluye", dentro de la caja destacada del total. */
	caption?: string;
}

const MoneyRow = ({
	title,
	valueUSD,
	exchangeRate,
	localCurrency,
	showLocalCurrency,
	primaryInLocalCurrency,
	highlight,
	sign,
	accentColor,
	caption,
}: MoneyRowProps): JSX.Element => {
	// La moneda local se calcula SIEMPRE sobre el valor USD sin redondear y se
	// redondea al final (separadorDeMiles ya redondea el USD para mostrarlo).
	// Redondear el USD antes de multiplicar da un COP distinto al del drawer y
	// al del resto de la app: en Colombia eso se veía como dos señas diferentes
	// para la misma habitación.
	const localValue = Math.round(valueUSD * exchangeRate);
	const primaryText = primaryInLocalCurrency
		? `${localCurrency} ${separadorDeMiles(localValue)}`
		: `USD ${separadorDeMiles(valueUSD)}`;
	const alternativeText = primaryInLocalCurrency
		? `USD ${separadorDeMiles(valueUSD)}`
		: `${localCurrency} ${separadorDeMiles(localValue)}`;

	return (
		<Box
			sx={{
				...(highlight && {
					backgroundColor: 'rgba(237, 102, 2, 0.08)',
					border: '1px solid rgba(237, 102, 2, 0.22)',
					borderRadius: '8px',
					padding: 1,
					marginTop: 1,
				}),
			}}
		>
			<Grid container alignItems="baseline">
				<Grid item xs={6}>
					<Typography
						fontSize={13}
						fontWeight={highlight ? 700 : 500}
						fontStyle={sign ? 'italic' : undefined}
						sx={{ color: sign ? SIGN_GREEN : undefined, lineHeight: 1.6 }}
					>
						{title}
					</Typography>
				</Grid>
				<Grid item xs={6} sx={{ textAlign: 'right' }}>
					<Typography
						fontSize={sign ? 13 : highlight ? 18 : 16}
						fontWeight={700}
						fontStyle={sign ? 'italic' : undefined}
						sx={{ color: sign ? SIGN_GREEN : accentColor || 'secondary.main', lineHeight: 1.4 }}
					>
						{primaryText}
					</Typography>
					{showLocalCurrency && (
						<Typography fontSize={12} sx={{ color: 'primary.main' }}>
							{alternativeText}
						</Typography>
					)}
				</Grid>
			</Grid>
			{/* La respuesta a "¿hay que sumarle el vuelo?" va pegada al monto: la
			    nota al pie no la leía nadie. */}
			{caption && (
				<Typography fontSize={12} sx={{ color: 'text.secondary', lineHeight: 1.35, mt: 0.5 }}>
					{caption}
				</Typography>
			)}
		</Box>
	);
};

export const RoomPricesSimplePreview = (props: Props): JSX.Element => {
	const { rooms, paxTotals, exchangeRate, localCurrency, showLocalCurrency, isExternal, showUtility, markup } = props;
	const { includedSummary } = props;
	const { auth } = useSelector((state: RootState) => state);
	const isColombia = auth.country === CountryCodeEnum.COLOMBIA;
	const signLabel = isColombia ? 'Reserva desde' : 'Seña';
	const accentColor = isExternal ? EXTERNAL_ACCENT_COLOR : undefined;

	return (
		// Mismo criterio de layout que multi: 1-2 habitaciones completas,
		// 3+ carrusel con flechas y contador (RoomCardsCarousel decide).
		<RoomCardsCarousel>
			{rooms.map((room, roomIndex) => {
				// Guarda explícita: si la config de habitaciones no coincide con las
				// tarifas (datos viejos o inconsistentes), no reventamos la página.
				const paxRoom = paxTotals.totalPaxPerRoom[roomIndex];
				const showChd = !!paxRoom?.hasChild;
				const showInf = !!paxRoom?.hasInfant;

				return (
					<Card
						key={`room-${roomIndex}`}
						sx={{
							height: '100%',
							borderRadius: '10px',
							border: '1px solid rgba(49, 25, 75, 0.12)',
							boxShadow: '0 8px 24px rgba(49, 25, 75, 0.10)',
						}}
					>
						<CardContent>
							<Typography
								fontWeight={700}
								textAlign="center"
								sx={{
									color: accentColor || 'primary.main',
									backgroundColor: 'rgba(49, 25, 75, 0.06)',
									borderRadius: '8px',
									p: 1,
									mb: 1,
								}}
							>
								Habitación {roomIndex + 1}
							</Typography>

							{paxRoom && (
								<Typography fontSize={13} fontWeight={600} sx={{ lineHeight: 1.6 }}>
									Se hospedan: {paxRoom.totalAdults} {paxRoom.totalAdults > 1 ? 'adultos' : 'adulto'}
									{paxRoom.hasChild && ` + ${paxRoom.totalChild} ${paxRoom.totalChild > 1 ? 'niños' : 'niño'}`}
									{paxRoom.hasInfant && ` + ${paxRoom.totalInfant} ${paxRoom.totalInfant > 1 ? 'bebés' : 'bebé'}`}
								</Typography>
							)}

							{/* Sin truncar: el tipo de habitación es información de verificación. */}
							{room.description && (
								<Typography fontSize={13} color="text.secondary" sx={{ lineHeight: 1.6 }}>
									Tipo de habitación: {room.description}
								</Typography>
							)}

							<Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mt={0.75} mb={0.5}>
								<Chip
									size="small"
									label={getBoardName(room.board)}
									sx={{ height: 24, borderRadius: '999px', fontWeight: 700, fontSize: 11 }}
								/>
								<Chip
									size="small"
									variant="outlined"
									label={room.rateType === 'nonRefundable' ? 'Tarifa no reembolsable' : 'Tarifa reembolsable'}
									sx={{
										height: 24,
										borderRadius: '999px',
										fontWeight: 700,
										fontSize: 11,
										color: room.rateType === 'nonRefundable' ? '#a83232' : SIGN_GREEN,
										borderColor:
											room.rateType === 'nonRefundable' ? 'rgba(168, 50, 50, 0.4)' : 'rgba(46, 125, 50, 0.4)',
									}}
								/>
							</Box>

							<MoneyRow
								title="Precio por adulto"
								valueUSD={room.adtPrice}
								exchangeRate={exchangeRate}
								localCurrency={localCurrency}
								showLocalCurrency={showLocalCurrency}
								primaryInLocalCurrency={isColombia}
								accentColor={accentColor}
							/>
							{room.sign && (
								<MoneyRow
									title={signLabel}
									valueUSD={room.sign.adtSign}
									exchangeRate={exchangeRate}
									localCurrency={localCurrency}
									showLocalCurrency={showLocalCurrency}
									primaryInLocalCurrency={isColombia}
									sign
								/>
							)}

							{showChd && (
								<>
									<MoneyRow
										title="Precio por niño"
										valueUSD={room.chdPrice}
										exchangeRate={exchangeRate}
										localCurrency={localCurrency}
										showLocalCurrency={showLocalCurrency}
										primaryInLocalCurrency={isColombia}
										accentColor={accentColor}
									/>
									{room.sign && (
										<MoneyRow
											title={signLabel}
											valueUSD={room.sign.chdSign}
											exchangeRate={exchangeRate}
											localCurrency={localCurrency}
											showLocalCurrency={showLocalCurrency}
											primaryInLocalCurrency={isColombia}
											sign
										/>
									)}
								</>
							)}

							{showInf && (
								<>
									<MoneyRow
										title="Precio por bebé"
										valueUSD={room.infPrice}
										exchangeRate={exchangeRate}
										localCurrency={localCurrency}
										showLocalCurrency={showLocalCurrency}
										primaryInLocalCurrency={isColombia}
										accentColor={accentColor}
									/>
									{room.sign && (
										<MoneyRow
											title={signLabel}
											valueUSD={room.sign.infSign}
											exchangeRate={exchangeRate}
											localCurrency={localCurrency}
											showLocalCurrency={showLocalCurrency}
											primaryInLocalCurrency={isColombia}
											sign
										/>
									)}
								</>
							)}

							<Divider sx={{ my: 1.5 }} />

							<MoneyRow
								title={getRoomTotalLabel(rooms.length)}
								valueUSD={room.totalPrice}
								exchangeRate={exchangeRate}
								localCurrency={localCurrency}
								showLocalCurrency={showLocalCurrency}
								primaryInLocalCurrency={isColombia}
								highlight
								accentColor={accentColor}
								caption={includedSummary}
							/>
							{room.sign && (
								<MoneyRow
									title={`${signLabel} por la habitación`}
									valueUSD={room.sign.totalSign}
									exchangeRate={exchangeRate}
									localCurrency={localCurrency}
									showLocalCurrency={showLocalCurrency}
									primaryInLocalCurrency={isColombia}
									sign
								/>
							)}
							{/* Margen bruto: sólo vista del agente (showUtility). Mismo
							    componente y mismo texto ("mb:") que multi y sólo-vuelos. */}
							{showUtility && markup != null && (
								<Box mt={0.5}>
									<Utility price={room.totalPrice} markup={markup} show={showUtility} />
								</Box>
							)}
						</CardContent>
					</Card>
				);
			})}
		</RoomCardsCarousel>
	);
};

export default RoomPricesSimplePreview;
